import apiFetch from './client';
import type { ParkingSpotStatus, RentStatusMode } from './condominiums';

export type OwnershipType = 'OWNER' | 'CO_OWNER';

export interface ParkingSpotOwner {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  spotId: string;
  spotCode: string;
  spotLocation?: string;
  spotLevel?: string;
  locationX?: number | null;
  locationY?: number | null;
  locationWidth?: number | null;
  locationHeight?: number | null;
  spotStatus: ParkingSpotStatus;
  spotRentStatusMode: RentStatusMode;
  spotIsActive: boolean;
  condominiumId?: string;
  condominiumName?: string;
  ownershipType: OwnershipType;
  createdAt: string;
}

export type AvailabilityStatus = 'OPEN' | 'CANCELLED';

export type RecurrenceFrequency = 'WEEKLY' | 'DAILY';

export interface WindowRecurrence {
  frequency: RecurrenceFrequency;
  occurrences: number;
}

export interface AvailabilityWindow {
  id: string;
  spotId: string;
  spotCode: string;
  seriesId?: string | null;
  startAt: string;
  endAt: string;
  allowBorrow: boolean;
  allowRent: boolean;
  status: AvailabilityStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAvailabilityWindowRequest {
  spotId: string;
  startAt: string;
  endAt: string;
  allowBorrow?: boolean;
  allowRent?: boolean;
  recurrence?: WindowRecurrence;
}

export interface CreateAvailabilityWindowResponse extends AvailabilityWindow {
  recurrence?: { createdCount: number; skippedCount: number };
}

export interface UpdateAvailabilityWindowRequest {
  startAt?: string;
  endAt?: string;
  allowBorrow?: boolean;
  allowRent?: boolean;
  status?: AvailabilityStatus;
}

export type UsageType = 'BORROW' | 'RENT';
export type UsageStatus = 'PENDING' | 'ACTIVE' | 'CANCELLED' | 'FINISHED' | 'REJECTED';

export interface SpotUsage {
  id: string;
  spotId: string;
  spotCode: string;
  availabilityWindowId: string;
  userId: string;
  userEmail?: string;
  userName?: string;
  userApartmentNumber?: string | null;
  type: UsageType;
  startAt: string;
  endAt: string;
  actualStartAt?: string;
  actualEndAt?: string;
  status: UsageStatus;
  createdAt: string;
  updatedAt: string;
}

export async function getMyParkingSpots(): Promise<ParkingSpotOwner[]> {
  const data = await apiFetch<ParkingSpotOwner[]>('/my-parking-spots');
  return data ?? [];
}

export async function getAvailabilityWindowsBySpot(spotId: string): Promise<AvailabilityWindow[]> {
  const data = await apiFetch<AvailabilityWindow[]>(`/availability-windows/spot/${spotId}`);
  return data ?? [];
}

export async function createAvailabilityWindow(
  request: CreateAvailabilityWindowRequest,
): Promise<CreateAvailabilityWindowResponse> {
  return apiFetch<CreateAvailabilityWindowResponse>('/availability-windows', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function updateAvailabilityWindow(
  windowId: string,
  request: UpdateAvailabilityWindowRequest,
): Promise<AvailabilityWindow> {
  return apiFetch<AvailabilityWindow>(`/availability-windows/${windowId}`, {
    method: 'PUT',
    body: JSON.stringify(request),
  });
}

export async function cancelAvailabilityWindow(windowId: string): Promise<void> {
  await apiFetch<void>(`/availability-windows/${windowId}`, { method: 'DELETE' });
}

export async function cancelAvailabilityWindowSeries(
  seriesId: string,
): Promise<{ cancelledCount: number }> {
  return apiFetch<{ cancelledCount: number }>(`/availability-windows/series/${seriesId}`, {
    method: 'DELETE',
  });
}

export async function getMySpotUsages(): Promise<SpotUsage[]> {
  const data = await apiFetch<SpotUsage[]>('/spot-usages/my-usages');
  return data ?? [];
}

export async function getSpotUsages(spotId: string): Promise<SpotUsage[]> {
  const data = await apiFetch<SpotUsage[]>(`/spot-usages/my-parking-spots/${spotId}/usages`);
  return data ?? [];
}

export async function getPendingRequests(spotId: string): Promise<SpotUsage[]> {
  const data = await apiFetch<SpotUsage[]>(`/spot-usages/my-parking-spots/${spotId}/pending`);
  return data ?? [];
}

export async function approveSpotUsage(usageId: string): Promise<void> {
  await apiFetch<void>(`/spot-usages/${usageId}/approve`, { method: 'PUT' });
}

export async function rejectSpotUsage(usageId: string): Promise<void> {
  await apiFetch<void>(`/spot-usages/${usageId}/reject`, { method: 'PUT' });
}

export async function cancelSpotUsage(usageId: string): Promise<void> {
  await apiFetch<void>(`/spot-usages/${usageId}/cancel`, { method: 'PUT' });
}

export async function finishSpotUsage(usageId: string): Promise<void> {
  await apiFetch<void>(`/spot-usages/${usageId}/finish`, { method: 'PUT' });
}

export async function reclaimSpotUsage(usageId: string): Promise<void> {
  await apiFetch<void>(`/spot-usages/${usageId}/reclaim`, { method: 'PUT' });
}

export interface BulkSpotRequest {
  id: string;
  startAt: string;
  endAt: string;
  type: UsageType;
  expired: boolean;
  expiresAt: string;
  createdAt: string;
}

export async function getMyBulkRequests(): Promise<BulkSpotRequest[]> {
  const data = await apiFetch<BulkSpotRequest[]>('/spot-usages/my-bulk-requests');
  return data ?? [];
}

export async function cancelBulkRequest(bulkRequestId: string): Promise<void> {
  await apiFetch<void>(`/spot-usages/bulk-request/${bulkRequestId}/cancel`, {
    method: 'PUT',
  });
}

export const myParkingSpotsKeys = {
  all: ['my-parking-spots'] as const,
  list: (condominiumId?: string) =>
    [...myParkingSpotsKeys.all, 'list', condominiumId ?? 'none'] as const,
  borrowed: (condominiumId?: string) =>
    [...myParkingSpotsKeys.all, 'borrowed', condominiumId ?? 'none'] as const,
  windows: (spotId: string) => [...myParkingSpotsKeys.all, 'windows', spotId] as const,
  usages: (spotId: string) => [...myParkingSpotsKeys.all, 'usages', spotId] as const,
  pending: (spotId: string) => [...myParkingSpotsKeys.all, 'pending', spotId] as const,
  history: (condominiumId?: string) =>
    [...myParkingSpotsKeys.all, 'history', condominiumId ?? 'none'] as const,
  bulkRequests: () => [...myParkingSpotsKeys.all, 'bulk-requests'] as const,
};
